import { useEffect, useState } from "react"

export const ESSENTIALS = "essentials" // URL value for products with no subcategory

export const typeLabel = (value) =>
  value === ESSENTIALS || value === "" ? "Kitchen Essentials" : value

const PRICE_PRESETS = [
  { label: "Under ₱1,000", max: 1000 },
  { label: "₱1,000 – ₱3,000", min: 1000, max: 3000 },
  { label: "₱3,000 – ₱5,000", min: 3000, max: 5000 },
  { label: "Over ₱5,000", min: 5000 },
]

const MOQ_OPTIONS = [15, 20, 30]

/**
 * Full-width filter panel shown above the products. Fully controlled:
 * `value` comes from the URL and every change goes through `onChange(patch)`.
 */
export default function FilterPanel({ value, facets, categories, onChange, onReset }) {
  const [search, setSearch] = useState(value.search)
  const [minPrice, setMinPrice] = useState(value.min)
  const [maxPrice, setMaxPrice] = useState(value.max)

  // Keep the text boxes in sync when the URL changes (back button, chips)
  useEffect(() => setSearch(value.search), [value.search])
  useEffect(() => setMinPrice(value.min), [value.min])
  useEffect(() => setMaxPrice(value.max), [value.max])

  // Search as you type, after a short pause
  useEffect(() => {
    if (search === value.search) return
    const t = setTimeout(() => onChange({ search }), 400)
    return () => clearTimeout(t)
  }, [search, value.search, onChange])

  /* ---------- Type (subcategory) ---------- */
  const counts = Object.fromEntries(
    (facets?.types || []).map((t) => [t.value === "" ? ESSENTIALS : t.value, t.count]),
  )
  const category = categories.find((c) => c.name === value.category)
  // Types only exist inside a category that has subcategories (e.g. Restaurant, Construction)
  const cuisines = (category?.subCategories || [])
    .filter((t) => counts[t])
    .sort((a, b) => a.localeCompare(b))
  const showType = cuisines.length > 0
  const hasEssentials = showType && counts[ESSENTIALS] > 0

  const cuisine = value.subs.find((s) => s !== ESSENTIALS) || ""
  const withEssentials = value.subs.includes(ESSENTIALS)
  const typeValue = cuisine || (withEssentials ? ESSENTIALS : "")

  const setType = (next) => {
    if (!next) return onChange({ subs: [] })
    if (next === ESSENTIALS) return onChange({ subs: [ESSENTIALS] })
    // A cuisine comes with the kitchen essentials unless they untick it
    onChange({ subs: hasEssentials ? [next, ESSENTIALS] : [next] })
  }

  const toggleEssentials = () =>
    onChange({ subs: withEssentials ? [cuisine] : [cuisine, ESSENTIALS] })

  /* ---------- Price ---------- */
  const applyPrice = () => {
    if (minPrice !== value.min || maxPrice !== value.max)
      onChange({ min: minPrice, max: maxPrice })
  }

  const presetActive = (p) =>
    String(p.min ?? "") === value.min && String(p.max ?? "") === value.max

  return (
    <div className="card card-body bg-light border-0 mb-3 text-start">
      <div className="row g-3">
        {/* SEARCH */}
        <div className="col-12 col-lg-4">
          <label htmlFor="filter-search" className="form-label fw-semibold">
            Search
          </label>
          <input
            id="filter-search"
            type="search"
            className="form-control"
            placeholder="e.g. chicken, soy sauce, napkins"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* CATEGORY */}
        <div className="col-12 col-sm-6 col-lg-2">
          <label htmlFor="filter-category" className="form-label fw-semibold">
            Category
          </label>
          <select
            id="filter-category"
            className="form-select"
            value={value.category}
            onChange={(e) => onChange({ category: e.target.value, subs: [] })}
          >
            <option value="">All categories</option>
            {categories.map((c) => (
              <option key={c._id} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* TYPE (only for categories with subcategories) */}
        {showType && (
          <div className="col-12 col-sm-6 col-lg-3">
            <label htmlFor="filter-type" className="form-label fw-semibold">
              Type
            </label>
            <select
              id="filter-type"
              className="form-select"
              value={typeValue}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="">All types</option>
              {hasEssentials && (
                <option value={ESSENTIALS}>
                  Kitchen Essentials ({counts[ESSENTIALS]})
                </option>
              )}
              {cuisines.map((t) => (
                <option key={t} value={t}>
                  {t} ({counts[t]})
                </option>
              ))}
            </select>

            {cuisine && hasEssentials && (
              <div className="form-check mt-1">
                <input
                  id="filter-essentials"
                  type="checkbox"
                  className="form-check-input"
                  checked={withEssentials}
                  onChange={toggleEssentials}
                />
                <label htmlFor="filter-essentials" className="form-check-label small">
                  Include kitchen essentials
                </label>
              </div>
            )}
          </div>
        )}

        {/* MOQ */}
        <div className="col-12 col-sm-6 col-lg-3">
          <label htmlFor="filter-moq" className="form-label fw-semibold">
            Minimum order (MOQ)
          </label>
          <select
            id="filter-moq"
            className="form-select"
            value={value.moq}
            onChange={(e) => onChange({ moq: e.target.value })}
          >
            <option value="">Any</option>
            {MOQ_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n} or less
              </option>
            ))}
          </select>
        </div>

        {/* PRICE */}
        <div className="col-12">
          <span className="form-label fw-semibold d-block">Price</span>
          <div className="d-flex flex-wrap align-items-center gap-2">
            {PRICE_PRESETS.map((p) => (
              <button
                key={p.label}
                type="button"
                className={`btn btn-sm ${
                  presetActive(p) ? "btn-success" : "btn-outline-secondary"
                }`}
                aria-pressed={presetActive(p)}
                onClick={() =>
                  onChange(
                    presetActive(p)
                      ? { min: "", max: "" }
                      : { min: String(p.min ?? ""), max: String(p.max ?? "") },
                  )
                }
              >
                {p.label}
              </button>
            ))}

            <span className="text-muted mx-1">or</span>

            <form
              className="d-flex flex-wrap align-items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                applyPrice()
              }}
            >
              <div className="input-group" style={{ width: "11rem" }}>
                <span className="input-group-text">₱</span>
                <input
                  type="number"
                  min="0"
                  className="form-control"
                  placeholder="Min"
                  aria-label="Minimum price in pesos"
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  onBlur={applyPrice}
                />
              </div>
              <span className="text-muted">to</span>
              <div className="input-group" style={{ width: "11rem" }}>
                <span className="input-group-text">₱</span>
                <input
                  type="number"
                  min="0"
                  className="form-control"
                  placeholder="Max"
                  aria-label="Maximum price in pesos"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  onBlur={applyPrice}
                />
              </div>
              <button type="submit" className="btn btn-success">
                Apply
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="text-end mt-3">
        <button type="button" className="btn btn-link text-danger p-0" onClick={onReset}>
          Clear all filters
        </button>
      </div>
    </div>
  )
}
